// DocumentosPage.js — Tarjetas v3: botones por modo + modales (consulta/práctica) + reto destacado
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { consultaService, misPaginasService, retoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useVoice } from '../contexts/VoiceContext';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import DynamicLanding from '../components/landing/DynamicLanding';
import { usePageLayout } from '../contexts/PageLayoutContext';

const ICONS = {
  consult:   'M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z',
  analytics: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
  cert:      'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
  questions: 'M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z',
  mentor:    'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z',
  practica:  'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  reto:      'M12 1l3.22 6.53L22 8.55l-5 4.87 1.18 6.88L12 17.07 5.82 20.3 7 13.42 2 8.55l6.78-1.02L12 1z',
  close:     'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
};

const BACKEND_BASE = '/backend';

// IDs DEBEN coincidir con $doctorProfiles en backend/api/realtime-session.php.
// Nombres en español/médicos universales; especialidad/nivel/desc usan claves i18n.
const DOCTORES = [
  { id: 'sofia_campos',    name: 'Dra. Sofía Campos',    specialtyKey: 'especialidad_general',     difficulty: 1, levelKey: 'nivel_facil',      emoji: '👩‍⚕️', color: '#10B981', descKey: 'doctor_sofia_desc' },
  { id: 'ricardo_navarro', name: 'Dr. Ricardo Navarro',  specialtyKey: 'especialidad_cardio',      difficulty: 2, levelKey: 'nivel_media',      emoji: '👨‍⚕️', color: '#F59E0B', descKey: 'doctor_ricardo_desc' },
  { id: 'elena_vega',      name: 'Dra. Elena Vega',      specialtyKey: 'especialidad_endocrino',   difficulty: 3, levelKey: 'nivel_media_alta', emoji: '👩‍⚕️', color: '#EF4444', descKey: 'doctor_elena_desc' },
  { id: 'marcos_fuentes',  name: 'Dr. Marcos Fuentes',   specialtyKey: 'especialidad_interna',     difficulty: 4, levelKey: 'nivel_dificil',    emoji: '🧑‍⚕️', color: '#7C3AED', descKey: 'doctor_marcos_desc' },
];

// ═════════ animations ═════════
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.55); }
  100% { box-shadow: 0 0 0 12px rgba(251, 191, 36, 0); }
`;
const overlayIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const modalIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
`;

// ═════════ layout ═════════
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0c1220;
  background-image:
    radial-gradient(ellipse at 20% 0%,   rgba(8,145,178,0.10) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 100%, rgba(20,182,203,0.06) 0%, transparent 60%);
`;
const Container = styled.div`
  padding: 3rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  @media (max-width: 767px) { padding: 1.5rem 0.875rem; }
`;
const PageHeader = styled.header`
  text-align: center;
  margin-bottom: 2.25rem;
  animation: ${fadeInUp} 0.5s ease-out;
`;
const PageTitle = styled.h1`
  font-size: clamp(1.65rem, 4vw, 2.6rem);
  font-weight: 800;
  color: #F1F5F9;
  margin: 0 0 0.4rem;
  letter-spacing: -0.02em;
`;
const PageSubtitle = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  color: #94A3B8;
  margin: 0;
`;
// Barra para encontrar un contenido cuando hay muchos: buscar por nombre,
// filtrar por proyecto (solo admin, que los ve todos) y paginar.
const Barra = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  margin-bottom: 1.25rem;
`;
const Buscador = styled.input`
  flex: 1 1 260px;
  min-width: 0;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.6);
  color: #E2E8F0;
  font-size: 0.95rem;
  &::placeholder { color: #64748B; }
  &:focus { outline: 2px solid #38BDF8; outline-offset: 1px; }
`;
const Selector = styled.select`
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.6);
  color: #E2E8F0;
  font-size: 0.95rem;
  &:focus { outline: 2px solid #38BDF8; outline-offset: 1px; }
  option { color: #0F172A; }
`;
const Cuenta = styled.span`
  color: #94A3B8;
  font-size: 0.88rem;
`;
const Paginacion = styled.nav`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  margin-top: 1.75rem;
  color: #94A3B8;
  font-size: 0.9rem;
  button {
    font: inherit;
    padding: 0.45rem 0.9rem;
    border-radius: 9px;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background: rgba(15, 23, 42, 0.6);
    color: #E2E8F0;
    cursor: pointer;
  }
  button:disabled { opacity: 0.4; cursor: not-allowed; }
  button:focus-visible { outline: 2px solid #38BDF8; outline-offset: 1px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 1.25rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.9rem;
  }
`;

// ═════════ Card ═════════
const Card = styled.article`
  position: relative;
  background: linear-gradient(160deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.85) 100%);
  border: 1px solid rgba(51, 65, 85, 0.45);
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
              border-color 0.3s, box-shadow 0.3s;
  animation: ${fadeInUp} 0.5s ease-out both;
  animation-delay: ${p => p.$delay || '0s'};
  &:hover {
    transform: translateY(-4px);
    border-color: rgba(34, 211, 238, 0.4);
    box-shadow: 0 14px 40px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }
  &:hover img { transform: scale(1.04); }
`;
const Hero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #0e1a2a 0%, #182838 100%);
  overflow: hidden;
`;
const HeroImage = styled.img`
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
`;
const HeroPlaceholder = styled.div`
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: rgba(148, 163, 184, 0.22);
  font-size: 5rem; font-weight: 900;
  font-family: 'Playfair Display', Georgia, serif;
  letter-spacing: -2px;
`;
const HeroOverlay = styled.div`
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(8, 12, 22, 0.94) 0%, rgba(8, 12, 22, 0.30) 55%, transparent 100%);
  display: flex; flex-direction: column; justify-content: flex-end;
  padding: 1rem 1.1rem 0.9rem;
  pointer-events: none;
`;
const HeroTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #F8FAFC;
  line-height: 1.25;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const ChipRow = styled.div`
  position: absolute;
  top: 0.7rem; right: 0.7rem;
  display: flex; gap: 0.35rem; flex-wrap: wrap; justify-content: flex-end;
  max-width: calc(100% - 1.4rem);
  pointer-events: none;
  z-index: 2;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.66rem; font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: ${p => p.$bg || 'rgba(15, 23, 42, 0.82)'};
  color: ${p => p.$color || '#E2E8F0'};
  border: 1px solid ${p => p.$border || 'rgba(255, 255, 255, 0.12)'};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;
const Body = styled.div`
  padding: 0.95rem 1.1rem 1.1rem;
  display: flex; flex-direction: column; gap: 0.85rem;
  flex: 1;
`;
const Description = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #94A3B8;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.65em;
`;

// ═════════ Actions ═════════
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: auto;
`;
const PrimaryAction = styled.button`
  flex: 1 1 100%;
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px;
  padding: 0.72rem 1rem;
  border-radius: 11px;
  font-weight: 700; font-size: 0.92rem;
  border: none; cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #06B6D4 0%, #0891B2 100%);
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
  transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
  &:hover {
    background: linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%);
    box-shadow: 0 6px 18px rgba(34, 211, 238, 0.42);
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
`;
const SecondaryAction = styled(Link)`
  flex: 1 1 calc(50% - 0.225rem);
  min-width: 0;
  display: inline-flex; align-items: center; justify-content: center;
  gap: 6px;
  padding: 0.58rem 0.6rem;
  border-radius: 10px;
  font-weight: 600; font-size: 0.82rem;
  text-decoration: none;
  color: ${p => p.$color || '#94A3B8'};
  background: ${p => p.$bg || 'rgba(51, 65, 85, 0.35)'};
  border: 1px solid ${p => p.$border || 'rgba(51, 65, 85, 0.6)'};
  transition: all 0.18s;
  white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
  &:hover {
    background: ${p => p.$hoverBg || 'rgba(51, 65, 85, 0.55)'};
    border-color: ${p => p.$color || '#94A3B8'};
    transform: translateY(-1px);
  }
`;
// Versión button (para Práctica que abre modal en vez de navegar)
const SecondaryButton = styled.button`
  flex: 1 1 calc(50% - 0.225rem);
  min-width: 0;
  display: inline-flex; align-items: center; justify-content: center;
  gap: 6px;
  padding: 0.58rem 0.6rem;
  border-radius: 10px;
  font-weight: 600; font-size: 0.82rem;
  cursor: pointer;
  color: ${p => p.$color || '#94A3B8'};
  background: ${p => p.$bg || 'rgba(51, 65, 85, 0.35)'};
  border: 1px solid ${p => p.$border || 'rgba(51, 65, 85, 0.6)'};
  transition: all 0.18s;
  white-space: nowrap;
  &:hover {
    background: ${p => p.$hoverBg || 'rgba(51, 65, 85, 0.55)'};
    border-color: ${p => p.$color || '#94A3B8'};
    transform: translateY(-1px);
  }
`;
// Versión "Reto" con anillo pulsante si hay nuevo
const RetoAction = styled(SecondaryAction)`
  ${p => p.$pendiente && css`
    color: #FBBF24;
    background: rgba(251, 191, 36, 0.18);
    border-color: rgba(251, 191, 36, 0.6);
    animation: ${pulseRing} 1.6s ease infinite;
  `}
`;
const IconAction = styled.button`
  flex: 0 0 40px;
  width: 40px; height: 40px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.08);
  color: #34D399;
  cursor: pointer;
  transition: all 0.18s;
  &:hover {
    background: rgba(16, 185, 129, 0.18);
    border-color: #10B981;
    transform: translateY(-1px);
  }
`;

// ═════════ Modal genérico ═════════
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(2, 6, 16, 0.78);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
  animation: ${overlayIn} 0.2s ease-out;
`;
const ModalShell = styled.div`
  background: linear-gradient(160deg, #0f172a 0%, #081020 100%);
  border: 1px solid rgba(51, 65, 85, 0.6);
  border-radius: 18px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
  animation: ${modalIn} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.1rem 1.25rem 0.5rem;
`;
const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #F1F5F9;
  font-weight: 700;
`;
const CloseBtn = styled.button`
  background: rgba(255,255,255,0.06);
  color: #94A3B8;
  border: none; cursor: pointer;
  width: 32px; height: 32px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.18s;
  &:hover { background: rgba(255,255,255,0.12); color: #fff; }
`;
const ModalSub = styled.p`
  margin: 0 1.25rem 0.8rem;
  color: #94A3B8; font-size: 0.86rem;
`;
const ModalBody = styled.div`
  padding: 0.4rem 1.25rem 1.25rem;
`;
const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
`;
const OptionCard = styled.button`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid ${p => p.$borderColor || 'rgba(51, 65, 85, 0.6)'};
  border-radius: 14px;
  padding: 1rem 1rem;
  text-align: left;
  color: #E2E8F0;
  cursor: pointer;
  display: flex; flex-direction: column; gap: 0.45rem;
  transition: all 0.2s;
  &:hover {
    border-color: ${p => p.$accentColor || '#22D3EE'};
    background: rgba(15, 23, 42, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4);
  }
`;
const OptionTop = styled.div`
  display: flex; align-items: center; gap: 0.55rem;
`;
const OptionEmoji = styled.span`
  font-size: 1.7rem;
  line-height: 1;
`;
const OptionTitle = styled.div`
  font-weight: 700; font-size: 1rem;
  color: #F8FAFC;
`;
const OptionMeta = styled.div`
  font-size: 0.74rem; font-weight: 700;
  letter-spacing: 0.5px; text-transform: uppercase;
  color: ${p => p.$color || '#94A3B8'};
`;
const OptionDesc = styled.div`
  font-size: 0.85rem;
  color: #94A3B8;
  line-height: 1.4;
`;
const DiffDots = styled.div`
  display: inline-flex; gap: 3px;
`;
const Dot = styled.span`
  width: 7px; height: 7px;
  border-radius: 50%;
  background: ${p => p.$on ? (p.$color || '#22D3EE') : 'rgba(255,255,255,0.12)'};
`;

const Icon = ({ path, size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d={path} />
  </svg>
);

const IDIOMA_LABELS = { es: 'ES', en: 'EN', pt: 'PT' };

// ═════════ Component ═════════
const DocumentosPage = () => {
  const { user } = useAuth();
  const { speak } = useVoice();
  const navigate = useNavigate();
  const { proyecto } = useProject();
  const { t, idioma } = useIdioma();
  const { setPageLayout } = usePageLayout();
  const modosProyecto = proyecto?.config_json?.modos_habilitados || {};

  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customPage, setCustomPage] = useState(null);
  const [retosPendientes, setRetosPendientes] = useState({}); // { [docId]: true }
  const [consultaModalDoc, setConsultaModalDoc] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [pagina, setPagina] = useState(1);
  const [practicaModalDoc, setPracticaModalDoc] = useState(null);

  useEffect(() => {
    if (!proyecto?.id) { setCustomPage(false); return; }
    let cancel = false;
    misPaginasService.contenidos(proyecto.id, idioma)
      .then(res => { if (!cancel) setCustomPage((res.data?.paginas || [])[0] || false); })
      .catch(() => { if (!cancel) setCustomPage(false); });
    return () => { cancel = true; };
  }, [proyecto?.id, idioma, user]);

  // Si la página de contenidos trae su propio header/footer, avisar al layout
  // global para que no los duplique (mismo contrato que PaginaView).
  useEffect(() => {
    const secs = (customPage && customPage.secciones) || [];
    const hay = (tipo) => secs.some(s => s.tipo === tipo && s.visible !== false);
    setPageLayout({ pageHasHeader: hay('header'), pageHasFooter: hay('footer') });
    return () => setPageLayout({ pageHasHeader: false, pageHasFooter: false });
  }, [customPage, setPageLayout]);

  useEffect(() => {
    const cargarDocumentos = async () => {
      try {
        const response = await consultaService.getDocumentos();
        setDocumentos(response.data || []);
      } catch (err) {
        setError('Error al cargar los documentos');
      } finally {
        setLoading(false);
      }
    };
    cargarDocumentos();
  }, []);

  // Verificar qué documentos tienen reto pendiente — una vez cargados los docs.
  // Solo si el proyecto tiene el modo reto habilitado (default true).
  useEffect(() => {
    if (!user?.id || documentos.length === 0) return;
    if (modosProyecto.reto === false) return;
    let cancel = false;
    (async () => {
      const map = {};
      await Promise.all(documentos.map(async (d) => {
        try {
          const res = await retoService.verificarRetoPendiente(d.id, user.id);
          if (res?.data?.tiene_reto_pendiente) map[d.id] = true;
        } catch (_) { /* silencioso */ }
      }));
      if (!cancel) setRetosPendientes(map);
    })();
    return () => { cancel = true; };
  }, [documentos, user?.id, modosProyecto.reto]);

  const handleAnalytics = (documentId) => navigate(`/analytics/${documentId}`);
  const canViewAnalytics = user && (user.role === 'admin' || user.role === 'mentor');

  useEffect(() => {
    if (user && documentos.length > 0 && !loading) {
      const welcomeKey = `documentos_welcome_${user.id}`;
      const alreadyWelcomed = sessionStorage.getItem(welcomeKey);
      if (!alreadyWelcomed) {
        speak(`Hola ${user.nombre}, bienvenido. Selecciona un tema para comenzar.`);
        sessionStorage.setItem(welcomeKey, 'true');
      }
    }
  }, [user, documentos, loading, speak]);

  const elegirSubmodoConsulta = useCallback((submode) => {
    const doc = consultaModalDoc;
    if (!doc) return;
    setConsultaModalDoc(null);
    navigate(`/consulta/${doc.id}?modo=consulta&submode=${submode}`);
  }, [consultaModalDoc, navigate]);

  const elegirDoctorPractica = useCallback((doctorId) => {
    const doc = practicaModalDoc;
    if (!doc) return;
    setPracticaModalDoc(null);
    navigate(`/consulta/${doc.id}?modo=practica&doctor=${doctorId}`);
  }, [practicaModalDoc, navigate]);

  const docsIdioma = idioma
    ? documentos.filter(d => !d.idioma || d.idioma === idioma)
    : documentos;

  // Proyectos presentes en la lista (solo los ve el admin, que recibe todos los
  // contenidos de la plataforma).
  const proyectosDisponibles = (() => {
    const m = new Map();
    docsIdioma.forEach(d => (d.proyectos || []).forEach(p => m.set(String(p.id), p.nombre)));
    return [...m].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  })();

  const texto = busqueda.trim().toLowerCase();
  const docsFiltrados = docsIdioma.filter(d => {
    if (filtroProyecto) {
      if (filtroProyecto === 'sin') { if ((d.proyectos || []).length) return false; }
      else if (!(d.proyectos || []).some(p => String(p.id) === filtroProyecto)) return false;
    }
    if (!texto) return true;
    return [d.titulo, d.descripcion, ...(d.proyectos || []).map(p => p.nombre)]
      .filter(Boolean).join(' ').toLowerCase().includes(texto);
  });

  const POR_PAGINA = 12;
  const paginas = Math.max(1, Math.ceil(docsFiltrados.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, paginas);
  const docsVisibles = docsFiltrados.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);
  const hayBarra = docsIdioma.length > POR_PAGINA || proyectosDisponibles.length > 1;

  if (customPage === null) return (
    <PageWrapper>
      <Container style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <p style={{ color: '#64748B', fontSize: '1.1rem' }}>{t('cargando_contenidos')}</p>
      </Container>
    </PageWrapper>
  );
  if (customPage) {
    return <DynamicLanding secciones={customPage.secciones || []} proyecto={proyecto} />;
  }
  if (loading) return (
    <PageWrapper>
      <Container style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <p style={{ color: '#64748B', fontSize: '1.1rem' }}>{t('cargando_contenidos')}</p>
      </Container>
    </PageWrapper>
  );
  if (error) return (
    <PageWrapper>
      <Container>
        <p style={{ color: '#ef4444', textAlign: 'center' }}>{error}</p>
      </Container>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>{t('centro_contenidos')}</PageTitle>
          <PageSubtitle>{t('selecciona_tema')}</PageSubtitle>
        </PageHeader>

        {hayBarra && (
          <Barra role="search">
            <Buscador
              type="search"
              value={busqueda}
              onChange={e => { setBusqueda(e.target.value); setPagina(1); }}
              placeholder={t('buscar_contenido')}
              aria-label={t('buscar_contenido')}
            />
            {proyectosDisponibles.length > 1 && (
              <Selector value={filtroProyecto} onChange={e => { setFiltroProyecto(e.target.value); setPagina(1); }}
                        aria-label="Filtrar por proyecto">
                <option value="">{t('todos_los_proyectos')}</option>
                {proyectosDisponibles.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
                <option value="sin">{t('sin_proyecto')}</option>
              </Selector>
            )}
            <Cuenta>
              {docsFiltrados.length === docsIdioma.length
                ? `${docsIdioma.length} ${t('contenidos_cuenta')}`
                : `${docsFiltrados.length} / ${docsIdioma.length} ${t('contenidos_cuenta')}`}
            </Cuenta>
          </Barra>
        )}

        {docsFiltrados.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#64748B' }}>
            {texto || filtroProyecto ? t('sin_coincidencias') : t('no_contenidos')}
          </p>
        ) : (
          <Grid>
            {docsVisibles.map((doc, idx) => {
              const retoEnabled = modosProyecto.reto !== false;
              const practicaEnabled = modosProyecto.practica === true && Number(doc.modo_practica) === 1;
              const retoPendiente = !!retosPendientes[doc.id];

              return (
                <Card key={doc.id} $delay={`${idx * 0.06}s`}>
                  <Hero>
                    {doc.imagen ? (
                      <HeroImage
                        src={`${BACKEND_BASE}/${doc.imagen}`}
                        alt={doc.titulo}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <HeroPlaceholder>{(doc.titulo || '?').charAt(0).toUpperCase()}</HeroPlaceholder>
                    )}
                    <ChipRow>
                      {doc.idioma && (
                        <Chip $bg="rgba(139, 92, 246, 0.85)" $color="#fff" $border="rgba(167, 139, 250, 0.6)">
                          {IDIOMA_LABELS[doc.idioma] || doc.idioma}
                        </Chip>
                      )}
                      {doc.tiene_certificado && (
                        <Chip $bg="rgba(16, 185, 129, 0.88)" $color="#fff" $border="rgba(52, 211, 153, 0.6)">
                          <Icon path={ICONS.cert} size={10} />
                          {t('certificado')}
                        </Chip>
                      )}
                    </ChipRow>
                    <HeroOverlay>
                      <HeroTitle>{doc.titulo}</HeroTitle>
                    </HeroOverlay>
                  </Hero>

                  <Body>
                    <Description>
                      {doc.descripcion || t('sin_descripcion')}
                    </Description>

                    <Actions>
                      {modosProyecto.consulta !== false && (
                        <PrimaryAction onClick={() => setConsultaModalDoc(doc)}>
                          <Icon path={ICONS.consult} size={17} />
                          {t('consultar')}
                        </PrimaryAction>
                      )}

                      {Number(doc.modo_mentor) === 1 && modosProyecto.mentor !== false && (
                        <SecondaryAction
                          to={`/mentor/${doc.id}`}
                          $color="#FBBF24"
                          $bg="rgba(251, 191, 36, 0.08)"
                          $border="rgba(251, 191, 36, 0.3)"
                          $hoverBg="rgba(251, 191, 36, 0.18)"
                          title={t('mentor_2_titulo')}
                        >
                          <Icon path={ICONS.mentor} size={15} />
                          {t('mentor')}
                        </SecondaryAction>
                      )}

                      {Number(doc.modo_evaluacion) === 1 && modosProyecto.evaluacion !== false && (
                        <SecondaryAction
                          to={`/evaluacion/${doc.id}`}
                          $color="#F472B6"
                          $bg="rgba(244, 114, 182, 0.08)"
                          $border="rgba(244, 114, 182, 0.3)"
                          $hoverBg="rgba(244, 114, 182, 0.18)"
                          title={`${doc.preguntas_por_evaluacion || 10} ${t('preguntas')} · ${doc.max_intentos || 3} ${t('intentos')}`}
                        >
                          <Icon path={ICONS.questions} size={15} />
                          {t('evaluacion') || 'Evaluación'}
                        </SecondaryAction>
                      )}

                      {practicaEnabled && (
                        <SecondaryButton
                          onClick={() => setPracticaModalDoc(doc)}
                          $color="#22D3EE"
                          $bg="rgba(34, 211, 238, 0.08)"
                          $border="rgba(34, 211, 238, 0.3)"
                          $hoverBg="rgba(34, 211, 238, 0.18)"
                          title={t('practica_visita_medica')}
                        >
                          <Icon path={ICONS.practica} size={15} />
                          {t('practica')}
                        </SecondaryButton>
                      )}

                      {retoEnabled && (
                        <RetoAction
                          to={`/reto/${doc.id}`}
                          $color={retoPendiente ? '#FBBF24' : '#94A3B8'}
                          $bg={retoPendiente ? 'rgba(251, 191, 36, 0.18)' : 'rgba(51, 65, 85, 0.35)'}
                          $border={retoPendiente ? 'rgba(251, 191, 36, 0.6)' : 'rgba(51, 65, 85, 0.6)'}
                          $hoverBg={retoPendiente ? 'rgba(251, 191, 36, 0.28)' : 'rgba(51, 65, 85, 0.55)'}
                          $pendiente={retoPendiente}
                          title={retoPendiente ? t('reto_pendiente_tooltip') : t('reto_semanal')}
                        >
                          <Icon path={ICONS.reto} size={15} />
                          {retoPendiente ? t('reto_nuevo') : t('reto')}
                        </RetoAction>
                      )}

                      {canViewAnalytics && (
                        <IconAction onClick={() => handleAnalytics(doc.id)} title={t('analiticas')} aria-label={t('analiticas')}>
                          <Icon path={ICONS.analytics} size={19} />
                        </IconAction>
                      )}
                    </Actions>
                  </Body>
                </Card>
              );
            })}
          </Grid>
        )}

        {paginas > 1 && (
          <Paginacion aria-label="Páginas de contenidos">
            <button type="button" disabled={paginaActual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
              ← {t('anterior')}
            </button>
            <span>{t('pagina_de')} {paginaActual}/{paginas}</span>
            <button type="button" disabled={paginaActual === paginas} onClick={() => setPagina(p => Math.min(paginas, p + 1))}>
              {t('siguiente')} →
            </button>
          </Paginacion>
        )}
      </Container>

      {/* ═════ Modal Consulta: 2 submodos ═════ */}
      {consultaModalDoc && (
        <Overlay onClick={() => setConsultaModalDoc(null)}>
          <ModalShell onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('modal_consulta_titulo')} · {consultaModalDoc.titulo}</ModalTitle>
              <CloseBtn onClick={() => setConsultaModalDoc(null)} aria-label={t('cerrar')}><Icon path={ICONS.close} size={18} /></CloseBtn>
            </ModalHeader>
            <ModalSub>{t('modal_consulta_sub')}</ModalSub>
            <ModalBody>
              <OptionGrid>
                <OptionCard
                  $borderColor="rgba(6, 182, 212, 0.4)"
                  $accentColor="#22D3EE"
                  onClick={() => elegirSubmodoConsulta('uno_a_uno')}
                >
                  <OptionTop>
                    <OptionEmoji>🗣️</OptionEmoji>
                    <div>
                      <OptionTitle>{t('submode_uno_a_uno')}</OptionTitle>
                      <OptionMeta $color="#67E8F9">{t('submode_uno_a_uno_meta')}</OptionMeta>
                    </div>
                  </OptionTop>
                  <OptionDesc>{t('submode_uno_a_uno_desc')}</OptionDesc>
                </OptionCard>

                <OptionCard
                  $borderColor="rgba(139, 92, 246, 0.4)"
                  $accentColor="#A78BFA"
                  onClick={() => elegirSubmodoConsulta('grupal')}
                >
                  <OptionTop>
                    <OptionEmoji>👥</OptionEmoji>
                    <div>
                      <OptionTitle>{t('submode_grupal')}</OptionTitle>
                      <OptionMeta $color="#A78BFA">{t('submode_grupal_meta')}</OptionMeta>
                    </div>
                  </OptionTop>
                  <OptionDesc>{t('submode_grupal_desc')}</OptionDesc>
                </OptionCard>
              </OptionGrid>
            </ModalBody>
          </ModalShell>
        </Overlay>
      )}

      {/* ═════ Modal Práctica: 4 doctores ═════ */}
      {practicaModalDoc && (
        <Overlay onClick={() => setPracticaModalDoc(null)}>
          <ModalShell onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('modal_practica_titulo')} · {practicaModalDoc.titulo}</ModalTitle>
              <CloseBtn onClick={() => setPracticaModalDoc(null)} aria-label={t('cerrar')}><Icon path={ICONS.close} size={18} /></CloseBtn>
            </ModalHeader>
            <ModalSub>{t('modal_practica_sub')}</ModalSub>
            <ModalBody>
              <OptionGrid>
                {DOCTORES.map((d) => (
                  <OptionCard
                    key={d.id}
                    $borderColor={`${d.color}55`}
                    $accentColor={d.color}
                    onClick={() => elegirDoctorPractica(d.id)}
                  >
                    <OptionTop>
                      <OptionEmoji>{d.emoji}</OptionEmoji>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <OptionTitle>{d.name}</OptionTitle>
                        <OptionMeta $color={d.color}>{t(d.specialtyKey)}</OptionMeta>
                      </div>
                    </OptionTop>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DiffDots>
                        {[1, 2, 3, 4].map((n) => (
                          <Dot key={n} $on={n <= d.difficulty} $color={d.color} />
                        ))}
                      </DiffDots>
                      <span style={{ fontSize: '0.74rem', color: d.color, fontWeight: 700 }}>{t(d.levelKey)}</span>
                    </div>
                    <OptionDesc>{t(d.descKey)}</OptionDesc>
                  </OptionCard>
                ))}
              </OptionGrid>
            </ModalBody>
          </ModalShell>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default DocumentosPage;
